import React from "react";
import { flexRender, Table } from "@tanstack/react-table";
import { styled } from "styled-components";
import {
  HiChevronUp,
  HiChevronDown,
  HiChevronUpDown,
  HiChevronRight,
} from "react-icons/hi2";

import { useVirtualizer } from '@tanstack/react-virtual';


export const CheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;

`;

export const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  display: none;
`;

export const CustomCheckbox = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid ${({ checked }) => (checked ? "gray" : "#1F1F1F")};
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

           @media (max-width: 768px) {
    margin-right: 24px !important;
  }

  svg {
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transform: scale(${({ checked }) => (checked ? 1 : 0.5)});
    transition: all 0.2s ease;
  }

  ${CheckboxWrapper}:hover & {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

export const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={14*0.6}
    height={10*0.6}
    viewBox="0 0 7 5"
    fill="none"

  >
    <path
      d="M6.5 0.5L2.375 4.5L0.5 2.68182"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const TableWrapper = styled.div`
  background-color: var(--primary-color);
  border-radius: 8px;
  color: var(--text-color);
  font-family: "Onest",  "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif;
  font-size: 13px;
     @media (max-width: 768px) {
    padding: 0;
    font-size: 12px;
  }
  line-height: 20px;
  overflow-x: auto;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    text-align: left;

  }

  th {
         @media (max-width: 768px) {

  }
    padding: 12px 14px;
    color: var(--text-secondary-color);
    font-weight: 500;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  td {
    padding: 14px;
       @media (max-width: 768px) {
  white-space: normal;        /* разрешаем перенос на новую строку */
  word-wrap: break-word;      /* старый синтаксис */
  overflow-wrap: break-word;  /* современный синтаксис */
  max-width: 8ch;
  }
       
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    white-space: nowrap;
  }

  th:hover {
    cursor: pointer;
    color: var(--text-color);
  }

  tr {
    transition: background-color 0.2s ease;

    &[data-expanded="true"] {
      background-color: rgba(255, 255, 255, 0.02);
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.02);
    }
  }
`;

interface DataTableProps<T> {
  table: Table<T>;
  columns: any;
  toggleRow: (id: number) => void;
  expandedRows: Set<number>;
  expanded: boolean;
  onExpandedUpdate: (expanded: boolean) => void;
  renderExpandedRow?: (row: T) => React.ReactNode;
  selectable?: boolean;
    setSelectedRows: (row: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  selectedRows: Set<number>;
  onSelectionChange?: (selectedIds: number[]) => void;
}


export const DataTable = <T extends { id: number }>({
  table,
  columns,
  toggleRow,
  expandedRows,
  after,
  onAfterClick,
  expanded,
  onExpandedUpdate,
  renderExpandedRow,
  selectable = false,
  setSelectedRows,
  selectedRows,
  customSorting = null,
  setCustomSorting = null,
  loading = false,
}: DataTableProps<T>) => {


  const toggleSelectRow =     (id: number) => {
      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        return newSet;
      });};
    
      const parentRef = React.useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: table.getRowModel().rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 40,
        overscan: 10,
    });

  const toggleSelectAll = () => {
    setSelectedRows((prev) => {
      const allIds = table.getRowModel().rows.map((r) => r.original.id);
      const allSelected = allIds.every((id) => prev.has(id));
      const newSet = allSelected ? new Set() : new Set(allIds);
      return newSet;
    });};

  return (
    <TableWrapper>
  <StyledTable>
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {selectable && (
            <th style={{ width: "32px", paddingLeft: "10px" }}>
              <CheckboxWrapper onClick={(e) => e.stopPropagation()}>
                <HiddenCheckbox
                  checked={
                    table.getRowModel().rows.length > 0 &&
                    table
                      .getRowModel()
                      .rows.every((r) => selectedRows.has(r.original.id))
                  }
                  onChange={toggleSelectAll}
                />
                <CustomCheckbox
                  checked={
                    table.getRowModel().rows.length > 0 &&
                    table
                      .getRowModel()
                      .rows.every((r) => selectedRows.has(r.original.id))
                  }
                >
                  <CheckIcon />
                </CustomCheckbox>
              </CheckboxWrapper>
            </th>
          )}

          {headerGroup.headers.filter((header) => header.column.id != "alive30min").map((header) => {
            const isExpandable = header.column.id === "alive";
            const isSorted = header.column.getIsSorted?.();

            if (isExpandable && expanded) {
              return (
                <React.Fragment key={header.id}>
                  <th style={{ width: "60px" }}                 onClick={
             
                    header.column.getToggleSortingHandler?.()

                }>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}

                    >
                      <HiChevronRight
                        size={16}
                                              onClick={(e) => {
                        e.stopPropagation();
                        onExpandedUpdate(!expanded);
                      }}
                        style={{
                          
                          transform: expanded
                            ? "scaleX(-1)"
                            : "rotate(180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      Сразу
                                     {isSorted === "asc" && <HiChevronUp size={16} />}
                  {isSorted === "desc" && <HiChevronDown size={16} />}
                  {!isSorted && <HiChevronUpDown size={16} />}
                    </span>
                   
                  </th>
                  <th   onClick={
             
                    () => setCustomSorting(customSorting == null ? "desc" : customSorting == "desc" ? "asc" : null)

                } style={{ width: "60px" }}>   <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}

                    >
                    
                      30 мин
                                     {customSorting === "asc" && <HiChevronUp size={16} />}
                  {customSorting === "desc" && <HiChevronDown size={16} />}
                  {!customSorting && <HiChevronUpDown size={16} />}
                    </span>
                   
                  </th>
                </React.Fragment>
              );
            }

            return (
              <th
                key={header.id}
                onClick={
             
                    header.column.getToggleSortingHandler?.()

                }
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                   
                    
                  {isSorted === "asc" && <HiChevronUp size={16} />}
                  {isSorted === "desc" && <HiChevronDown size={16} />}
                  {!isSorted && <HiChevronUpDown size={16} />}
                    
                  
                  {isExpandable && (
                    <HiChevronRight
                      size={16}
                      style={{
                        cursor: "pointer",
                        transform: expanded
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpandedUpdate(!expanded);
                      }}
                    />
                  )}
                  
                </span>
                
              </th>
            );
          })}
          {after ? <th style={{ width: "auto", padding: 0 }} /> : null}
        </tr>
      ))}
       

    </thead>

  <tbody>
  {loading ? (
    <tr>
      <td
        colSpan={
          table.getVisibleFlatColumns().length +
          (selectable ? 1 : 0) +
          (expanded ? 1 : 0) +
          (after ? 1 : 0)
        }
        style={{ textAlign: "center", padding: "16px" }}
      >
        Данные загружаются, подождите...
      </td>
    </tr>
  ) : (
    table.getRowModel().rows.map((row) => (
      <React.Fragment key={row.id}>
        <tr
          onClick={() => toggleRow(row.original.id)}
          data-expanded={expandedRows.has(row.original.id)}
        >
          {selectable && (
            <td style={{ width: "32px", paddingLeft: "10px" }}>
              <CheckboxWrapper onClick={(e) => e.stopPropagation()}>
                <HiddenCheckbox
                  checked={selectedRows.has(row.original.id)}
                  onChange={() => toggleSelectRow(row.original.id)}
                />
                <CustomCheckbox checked={selectedRows.has(row.original.id)}>
                  <CheckIcon />
                </CustomCheckbox>
              </CheckboxWrapper>
            </td>
          )}

          {row.getVisibleCells().map((cell) => {
            const isAliveCell = cell.column.id === "alive";

            if (isAliveCell && expanded) {
              return (
                <React.Fragment key={cell.id}>
                  <td style={{ width: 60 }}><span style={{color: "var(--text-active-color)"}}>{row.original.alive}</span> из {row.original.alive + row.original.dead_count}</td>
                  <td style={{ width: 60 }}><span style={{color: "var(--text-active-color)"}}>{row.original.alive30Min}</span> из {row.original.workingAccounts}</td>
                </React.Fragment>
              );
            }

            return (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}

          {after && (
            <td
              onClick={(e) => {
                e.stopPropagation();
                onAfterClick?.(row.original);
              }}
              style={{ cursor: "pointer", padding: 0, margin: 0 }}
            >
              {after(row.original)}
            </td>
          )}
        </tr>

        {expandedRows.has(row.original.id) && renderExpandedRow && (
          <tr>
            <td
              colSpan={
                row.getVisibleCells().length + 1 + (expanded ? 1 : 0) + (selectable ? 1 : 0)
              }
              style={{ padding: 0 }}
            >
              {renderExpandedRow(row.original)}
            </td>
          </tr>
        )}
      </React.Fragment>
    ))
  )}
</tbody>

  </StyledTable>
    </TableWrapper>
  );
};
